/**
 * Challenges Store
 * Manages challenge data and filtering
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  ChallengeWithBrand,
  Brand,
  ChallengeFilters,
  ChallengeStatus
} from '@/types'
import { challengeService } from '@/services/api'
import { t } from '@/i18n'

export const useChallengesStore = defineStore('challenges', () => {
  // State
  const challenges = ref<ChallengeWithBrand[]>([])
  const featuredChallenges = ref<ChallengeWithBrand[]>([])
  const selectedChallenge = ref<ChallengeWithBrand | null>(null)
  const brands = ref<Brand[]>([])
  const filters = ref<ChallengeFilters>({
    status: 'all',
    brandId: null,
    searchQuery: '',
  })
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const pagination = ref({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  })

  // Getters
  const activeChallenges = computed(() =>
    challenges.value.filter(c => c.status === 'active')
  )

  const finalizedChallenges = computed(() =>
    challenges.value.filter(c => c.status === 'finalized')
  )

  const hasMorePages = computed(() =>
    pagination.value.page < pagination.value.totalPages
  )

  const isFiltered = computed(() =>
    filters.value.status !== 'all' ||
    filters.value.brandId !== null ||
    (filters.value.searchQuery && filters.value.searchQuery.length > 0)
  )

  // Actions
  async function fetchChallenges(page: number = 1, append: boolean = false) {
    isLoading.value = true
    error.value = null

    try {
      const response = await challengeService.getChallenges(
        filters.value,
        page,
        pagination.value.pageSize
      )

      if (append) {
        challenges.value = [...challenges.value, ...response.data]
      } else {
        challenges.value = response.data
      }

      pagination.value = {
        page: response.page,
        pageSize: response.pageSize,
        total: response.total,
        totalPages: response.totalPages,
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('errors.loadChallenges')
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function fetchFeaturedChallenges(count: number = 4) {
    try {
      featuredChallenges.value = await challengeService.getFeaturedChallenges(count)
    } catch (e) {
      console.error('Error fetching featured challenges:', e)
    }
  }

  async function fetchChallenge(id: string) {
    isLoading.value = true
    error.value = null

    try {
      selectedChallenge.value = await challengeService.getChallenge(id)
      return selectedChallenge.value
    } catch (e) {
      error.value = e instanceof Error ? e.message : t('errors.loadChallenge')
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function fetchBrands() {
    try {
      brands.value = await challengeService.getBrands()
    } catch (e) {
      console.error('Error fetching brands:', e)
    }
  }

  async function fetchActiveBrands() {
    try {
      brands.value = await challengeService.getActiveBrands()
    } catch (e) {
      console.error('Error fetching active brands:', e)
    }
  }

  function setStatusFilter(status: 'all' | ChallengeStatus) {
    filters.value.status = status
    pagination.value.page = 1
    fetchChallenges()
  }

  function setBrandFilter(brandId: string | null) {
    filters.value.brandId = brandId
    pagination.value.page = 1
    fetchChallenges()
  }

  function setSearchQuery(query: string) {
    filters.value.searchQuery = query
    pagination.value.page = 1
    fetchChallenges()
  }

  function clearFilters() {
    filters.value = {
      status: 'all',
      brandId: null,
      searchQuery: '',
    }
    pagination.value.page = 1
    fetchChallenges()
  }

  async function loadMore() {
    if (!hasMorePages.value || isLoading.value) return
    await fetchChallenges(pagination.value.page + 1, true)
  }

  function selectChallenge(challenge: ChallengeWithBrand | null) {
    selectedChallenge.value = challenge
  }

  function clearError() {
    error.value = null
  }

  return {
    // State
    challenges,
    featuredChallenges,
    selectedChallenge,
    brands,
    filters,
    isLoading,
    error,
    pagination,

    // Getters
    activeChallenges,
    finalizedChallenges,
    hasMorePages,
    isFiltered,

    // Actions
    fetchChallenges,
    fetchFeaturedChallenges,
    fetchChallenge,
    fetchBrands,
    fetchActiveBrands,
    setStatusFilter,
    setBrandFilter,
    setSearchQuery,
    clearFilters,
    loadMore,
    selectChallenge,
    clearError,
  }
})
